/**
 * Bird chat surface events — entry points, data/visualisation blocks and tool confirmations.
 *
 * One slice of the `ANALYTICS_EVENTS` SSOT — see `../analytics.ts`, which
 * spreads every slice back into the single import surface call sites use.
 * The event NAME STRINGS are a PostHog dashboard contract: add and retire keys
 * here, never rename a value in place (see `rules/analytics.md`).
 */

export const CHAT_EVENTS = {
  // Mobile
  // ── Legacy "Bird" event names — DO NOT RENAME ──────────────────────────
  // The product was renamed Bird -> Bower (2026-09-12). These event NAME
  // VALUES are deliberately frozen: PostHog keys every insight, funnel and
  // dashboard series on the ingested name, so renaming in place orphans all
  // of them. The constant KEYS stay BIRD_* so key and value cannot drift
  // apart. If these ever must become "Bower ...", the migration is dual-emit
  // for one full reporting period, then retire — never an in-place rename.
  // Same applies to COLLECTION_EMPTY_ASK_BIRD, COMMAND_PALETTE_ASK_BIRD and
  // EQUIPMENT_ASK_BIRD_CLICKED below.
  BIRD_CHAT_OPENED: 'Bird Chat Opened',
  BIRD_LIVE_FAB_TAPPED: 'Bird Live FAB Tapped',

  // Bird Chat — Data & Visualisation
  CHART_RENDERED: 'Chart Rendered',
  CHART_EXPORTED: 'Chart Exported',
  /**
   * A sandbox-drawn figure rendered in a chat message. Properties: `format`
   * ('svg' | 'png' | 'pdf' | 'unknown'), derived from the signed URL's
   * pathname extension. NO filename and NO caption; both are UGC and can
   * carry PHI.
   */
  FIGURE_RENDERED: 'Figure Rendered',
  /**
   * A chat figure was downloaded. Properties: `format`, same derivation and
   * privacy constraint as FIGURE_RENDERED.
   */
  FIGURE_EXPORTED: 'Figure Exported',
  /**
   * A mermaid fence rendered as a diagram. Properties: `kind`, matched
   * against the closed `MERMAID_KINDS` allowlist in `MermaidBlock.tsx`
   * (`graph`, `sequenceDiagram`, `flowchart`, and so on), falling back to
   * `'other'` for anything that does not match. NOT the first
   * whitespace-delimited token of the source: that derivation was the PHI
   * leak this allowlist replaced, since mermaid's own comment stripping lets
   * a diagram's first line be arbitrary model prose. The diagram source,
   * node labels and anything derived from them are UGC and can carry PHI;
   * PostHog is not BAA-covered.
   */
  MERMAID_RENDERED: 'Mermaid Rendered',
  /**
   * A mermaid fence failed to parse and fell back to a code block. Same
   * `kind` property and privacy constraint as MERMAID_RENDERED. Tracked so a
   * systematic prompt problem is visible instead of silently degrading.
   */
  MERMAID_PARSE_FAILED: 'Mermaid Parse Failed',
  /**
   * A diagram left the thread — copied, downloaded, or opened full size in
   * the lightbox. Same `kind` allowlist and the same privacy constraint as
   * MERMAID_RENDERED: the source and its node labels are UGC that can carry
   * PHI, so nothing derived from them may ride along. `format` /
   * `destination` / `succeeded` mirror CHART_EXPORTED so the two blocks'
   * export funnels are comparable.
   */
  MERMAID_EXPORTED: 'Mermaid Exported',

  // Bird Chat — destructive-action approval.
  //
  // DISPLAYED fires when the Confirm/Cancel control actually renders; ANSWERED
  // when the researcher responds to it. Both exist because only ANSWERED did,
  // and a prompt that never rendered therefore emitted NOTHING — which is how
  // #1619 shipped every confirmation-gated tool dead (merge, delete, visual
  // reference set) and stayed undetected until a manual production repro.
  // DISPLAYED without a matching ANSWERED is abandonment; ANSWERED without
  // DISPLAYED is impossible, and a DISPLAYED rate that falls to zero while
  // merge/delete are still being asked for is that same regression, visible
  // in a day rather than a release.
  //
  // The cancel rate within ANSWERED remains the signal that the preview copy
  // is wrong.
  CHAT_TOOL_CONFIRMATION_DISPLAYED: 'Chat Tool Confirmation Displayed',
  CHAT_TOOL_CONFIRMATION_ANSWERED: 'Chat Tool Confirmation Answered',
  CHAT_TOOL_CHOICE_DISPLAYED: 'Chat Tool Choice Displayed',
  CHAT_TOOL_CHOICE_ANSWERED: 'Chat Tool Choice Answered',

  /**
   * The chat showed "still working" for a turn this tab is not streaming: one
   * turn runs per session, server-enforced. Properties: `reason`, one of 'refused'
   * (a send was turned away because a turn was still running),
   * 'opened_mid_turn' (the history showed a turn running when this tab loaded
   * the conversation: another tab or device, or the turn a reload left running,
   * whose answer the history read then picks up) or 'stopping'
   * (this tab pressed Stop and sent again before the server let the stopped
   * turn go: the refusal or read is about that turn, `STOPPING_WINDOW_MS`).
   * IDs and the reason only.
   */
  CHAT_TURN_STILL_WORKING_SHOWN: 'Chat Turn Still Working Shown',

  /**
   * The researcher opened the spreadsheet from a change card in chat.
   * Properties: `status` ('applied' | 'failed'), `change_count`,
   * `landed_count`. Counts only: never a sheet name, range, summary or detail,
   * all of which can name the researcher's own content.
   */
  SPREADSHEET_CHANGE_CARD_OPENED: 'Spreadsheet Change Card Opened',

  /**
   * The researcher clicked Undo on a spreadsheet change card. Properties:
   * `change_count`, `landed_count` only. No sheet name, range, summary or
   * detail.
   */
  SPREADSHEET_CHANGE_CARD_UNDO_CLICKED: 'Spreadsheet Change Card Undo Clicked',

  /**
   * The researcher unfolded a long change list on a spreadsheet change card.
   * Properties: `status`, `change_count`, `landed_count`, matching
   * SPREADSHEET_CHANGE_CARD_OPENED. Counts only.
   */
  SPREADSHEET_CHANGE_CARD_EXPANDED: 'Spreadsheet Change Card Expanded',

  /**
   * The undo request completed successfully and the card updated to reflect
   * it. Properties: `change_count`, `landed_count` only, matching
   * SPREADSHEET_CHANGE_CARD_UNDO_CLICKED. A failed or refused undo is not
   * this event; see the backend's `NATIVE_SPREADSHEET_CHANGE_UNDONE`
   * (`extraction-events.ts`) for the outcome-coded server-side record.
   */
  SPREADSHEET_CHANGE_CARD_UNDONE: 'Spreadsheet Change Card Undone',

  /**
   * The researcher removed the selected cells from a spreadsheet's context
   * chip, keeping the spreadsheet itself in context. Properties:
   * `entity_type` only. NO sheet name and NO range: both describe the
   * researcher's own data.
   */
  CHAT_CONTEXT_SELECTION_DISMISSED: 'Chat Context Selection Dismissed',

  /**
   * The researcher dropped workspace items (dragged from the grid, list or
   * sidebar tree) on the chat composer, adding them as context. Properties:
   * `item_count`, `added_count`, `folder_count`, `file_count`. Counts only,
   * never a name or an id list.
   */
  CHAT_CONTEXT_DROPPED: 'Chat Context Dropped',

  /**
   * One or more composer files failed to upload before a turn was sent (a
   * writer's artifact-id path). The turn still goes out with the files
   * that made it. Properties: `session_id`, `file_count`, `failed_count`,
   * `stage` ('reserve' | 'transfer' | 'finalize' | 'unknown') and
   * `error_category` (the upload's bounded code, or 'unexpected'). NEVER a
   * file name: it is UGC and can carry PHI.
   */
  CHAT_ATTACHMENTS_UPLOAD_FAILED: 'Chat Attachments Upload Failed',
} as const;
